import { API_ENDPOINTS } from "./apiEndpoints";
import { useState, useEffect, useRef } from "react";
import { authenticatedFetch, API_BASE, getToken } from "./api";
import SearchableSelect from "./SearchableSelect";
import "./ShopifyConverter.css";

// ─── Progress Bar Component ─────────────────────────────────────────────────
function ProgressBar({ percent, message, status }) {
  const isError = status === "error";
  const isDone  = status === "done";

  const barColor = isError
    ? "linear-gradient(90deg, #ff4d4d, #cc0000)"
    : isDone
    ? "linear-gradient(90deg, #22c55e, #16a34a)"
    : "linear-gradient(90deg, #6366f1, #8b5cf6, #06b6d4)";

  return (
    <div style={{ marginTop: "1.2rem" }}>
      {/* Label row */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "6px",
        fontSize: "0.85rem",
        color: isError ? "#f87171" : isDone ? "#4ade80" : "#a5b4fc",
        fontWeight: 500,
      }}>
        <span>{message}</span>
        <span style={{ fontVariantNumeric: "tabular-nums" }}>{percent}%</span>
      </div>

      {/* Track */}
      <div style={{
        width: "100%",
        height: "10px",
        borderRadius: "999px",
        background: "rgba(255,255,255,0.1)",
        overflow: "hidden",
      }}>
        {/* Fill */}
        <div style={{
          height: "100%",
          width: `${percent}%`,
          background: barColor,
          borderRadius: "999px",
          transition: "width 0.5s ease, background 0.3s ease",
          boxShadow: isDone ? "0 0 8px #22c55e88" : isError ? "none" : "0 0 10px #6366f188",
          backgroundSize: "200% 100%",
          animation: (!isDone && !isError) ? "shimmer 1.8s linear infinite" : "none",
        }} />
      </div>

      {/* Extra shimmer keyframes injected once */}
      <style>{`
        @keyframes shimmer {
          0%   { background-position: 200% center; }
          100% { background-position: -200% center; }
        }
      `}</style>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────
function PreprocessFile() {
  const [vendors, setVendors] = useState([]);
  const [vendor, setVendor]   = useState("");
  const [file, setFile]       = useState(null);

  // Job state
  const [jobId,   setJobId]   = useState(null);
  const [percent, setPercent] = useState(0);
  const [message, setMessage] = useState("");
  const [elapsed, setElapsed] = useState(0);
  const [jobStatus, setJobStatus] = useState("idle"); // idle | processing | done | error

  const pollRef = useRef(null);

  // Fetch vendor list
  useEffect(() => {
    async function fetchVendors() {
      try {
        const res  = await authenticatedFetch(API_ENDPOINTS.vendors);
        if (!res.ok) throw new Error("Failed to fetch vendors");
        const data = await res.json();
        setVendors(data.vendors || []);
      } catch (err) {
        console.error("Error fetching vendors:", err);
        setVendors([]);
      }
    }
    fetchVendors();
  }, []);

  // Poll for progress whenever jobId is set
  useEffect(() => {
    if (!jobId) return;

    pollRef.current = setInterval(async () => {
      try {
        const res  = await authenticatedFetch(API_ENDPOINTS.progress(jobId));
        if (!res.ok) return;
        const data = await res.json();

        setPercent(data.percent);
        setMessage(data.message);
        setElapsed(data.elapsed || 0);
        setJobStatus(data.status);

      if (data.status === "done" || data.status === "error") {
        clearInterval(pollRef.current);
        if (data.status === "done") triggerDownload(jobId);
      }
    } catch (err) {
      console.error("Polling error:", err);
    }
  }, 1500);

  return () => clearInterval(pollRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [jobId]);

  // Download the finished file
  async function triggerDownload(id) {
    try {
      const token = getToken();
      const res   = await fetch(`${API_BASE}${API_ENDPOINTS.download(id)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Download failed");

      const blob        = await res.blob();
      const contentType = res.headers.get("content-type") || "";

      let fileExt = "csv";
      if (contentType.includes("spreadsheetml")) fileExt = "xlsx";
      else if (contentType.includes("zip"))      fileExt = "zip";

      const filename =
        vendor.toLowerCase() === "colorsdress" && fileExt === "zip"
          ? "colorsdress_bundle.zip"
          : `${vendor}_preprocessed.${fileExt}`;

      const url = window.URL.createObjectURL(blob);
      const a   = document.createElement("a");
      a.href     = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download error:", err);
    }
  }

  const handlePreprocess = async () => {
    if (!vendor || !file) {
      alert("Please select a vendor and upload a file.");
      return;
    }

    // Reset state
    setJobId(null);
    setPercent(0);
    setMessage("Uploading file…");
    setJobStatus("processing");

    const formData = new FormData();
    formData.append("vendor_name", vendor);
    formData.append("file", file);

    try {
      const response = await authenticatedFetch(API_ENDPOINTS.preprocess, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || "Failed to start preprocessing.");
      }

      const data = await response.json();
      setJobId(data.job_id);
      setPercent(5);
      setMessage("Job started — processing…");
    } catch (err) {
      console.error(err);
      setJobStatus("error");
      setMessage(err.message || "Error starting job.");
    }
  };

  const isRunning = jobStatus === "processing";
  const isDone    = jobStatus === "done";
  const isError   = jobStatus === "error";

  return (
    <div className="converter-container">
      <h2>Vendor File Preprocessor</h2>

      <div className="form-group">
        <label>Vendor Name</label>
        <SearchableSelect
          options={vendors}
          value={vendor}
          onChange={(val) => setVendor(val)}
          placeholder="Select vendor..."
          disabled={isRunning}
        />
      </div>

      <div className="form-group">
        <label>Upload Vendor File</label>
        <input
          type="file"
          onChange={(e) => setFile(e.target.files[0])}
          disabled={isRunning}
        />
      </div>

      <button
        onClick={handlePreprocess}
        disabled={isRunning}
        style={isRunning ? { opacity: 0.6, cursor: "not-allowed" } : {}}
      >
        {isRunning ? "Processing…" : isDone ? "✅ Done — Process Again" : "Preprocess File"}
      </button>

      {/* ── Progress UI ── */}
      {jobStatus !== "idle" && (
        <div style={{ marginTop: "1rem" }}>
          <ProgressBar percent={percent} message={message} status={jobStatus} />
          {jobStatus === "processing" && (
            <p style={{ fontSize: "0.8rem", color: "#888", marginTop: "4px" }}>
              Time elapsed: {elapsed}s
            </p>
          )}
        </div>
      )}

      {/* Status badges */}
      {isDone && (
        <p style={{ marginTop: "0.75rem", color: "#4ade80", fontWeight: 600, fontSize: "0.9rem" }}>
          ✅ File downloaded successfully! (Took {elapsed}s)
        </p>
      )}
      {isError && (
        <p style={{ marginTop: "0.75rem", color: "#f87171", fontWeight: 600, fontSize: "0.9rem" }}>
          ❌ Error: {message}
        </p>
      )}
    </div>
  );
}

export default PreprocessFile;
